<template>
    <modal-dialog
        ref="socialMediaModal"
        description="Hier kun je de social media voor je vestiging instellen"
        label="Social media formulier"
    >
        <flex-container>
            <flex-container :gap="Size.sm" direction="column">
                <text-decorator bold :size="FontSize.xs">
                    Vestigingen
                </text-decorator>
                <text-heading>
                    Social media accounts beheren
                </text-heading>
                <text-description>
                    Vul de social media account bijhorend bij de vestiging om deze te koppelen.
                </text-description>
            </flex-container>

            <flex-container>
                <flex-container
                    :screen-sizes="{
                        laptop: 2,
                    }"
                >
                    <data-group>
                        <data-label>
                            Accounts
                        </data-label>
                        <data-value>
                            Geef de urls van de social media pagina's op
                        </data-value>
                    </data-group>
                </flex-container>

                <flex-container
                    :screen-sizes="{
                        laptop: 8,
                    }"
                >
                    <input-group
                        v-for="social in Object.keys(form)"
                        :key="social"
                        v-model="form[social]"
                    >
                        <template #input="{ bindings }">
                            <flex-container no-wrap align-items="center">
                                <label :for="bindings.ids.input">
                                    <svg-icon :icon="iconMap[social]" is-custom-icon />
                                </label>
                                <flex-container
                                    :screen-sizes="{
                                        laptop: 5,
                                    }"
                                >
                                    <text-input
                                        :placeholder="`https://www.${social.substring(0, social.length - 3)}.com/...`"
                                    />
                                </flex-container>
                            </flex-container>
                        </template>
                    </input-group>
                </flex-container>
            </flex-container>

            <flex-container
                v-if="socialMediaModal"
                justify-content="flex-end"
                align-items="center"
            >
                <button-text @click="cancel">
                    Terug
                </button-text>
                <button-block @loading="confirm(); $event.button.setState(ButtonState.completed)">
                    Bevestigen
                </button-block>
            </flex-container>
        </flex-container>
    </modal-dialog>
</template>

<script setup lang="ts">
    import FacebookIcon from '@/assets/icons/social/round/facebook.svg?component';
    import InstagramIcon from '@/assets/icons/social/square/instagram.svg?component';
    import LinkedinIcon from '@/assets/icons/social/square/linkedin.svg?component';
    import YoutubeIcon from '@/assets/icons/social/regular/youtube.svg?component';
    import XIcon from '@/assets/icons/social/square/x.svg?component';
    import PinterestIcon from '@/assets/icons/social/round/pinterest.svg?component';
    import TikTokIcon from '@/assets/icons/social/square/tiktok.svg?component';
    import VimeoIcon from '@/assets/icons/social/regular/vimeo.svg?component';

    import {
        type ExposedOverlay,
        Size,
        FontSize,
        ButtonState,
        ModalDialog,
        FlexContainer,
        TextDecorator,
        TextHeading,
        TextDescription,
        DataGroup,
        DataLabel,
        DataValue,
        InputGroup,
        SvgIcon,
        TextInput,
        ButtonText,
        ButtonBlock,
    } from '@keyvue/one-ui';
    import { ref } from 'vue';

    export interface Form {
        facebookUrl: string
        instagramUrl: string
        linkedinUrl: string
        youtubeUrl: string
        xUrl: string
        pinterestUrl: string
        tiktokUrl: string
        vimeoUrl: string
    }

    export interface ExposedSocialMediaForm {
        form: Form,
        setForm: (values: Form) => void,
        overlay: ExposedOverlay,
    }

    const iconMap = {
        facebookUrl: FacebookIcon,
        instagramUrl: InstagramIcon,
        linkedinUrl: LinkedinIcon,
        youtubeUrl: YoutubeIcon,
        xUrl: XIcon,
        pinterestUrl: PinterestIcon,
        tiktokUrl: TikTokIcon,
        vimeoUrl: VimeoIcon,
    };

    const socialMediaModal = ref<ExposedOverlay>();

    const form = ref<Form>({
        facebookUrl: '',
        instagramUrl: '',
        linkedinUrl: '',
        youtubeUrl: '',
        xUrl: '',
        pinterestUrl: '',
        tiktokUrl: '',
        vimeoUrl: '',
    });

    const emit = defineEmits(['confirmForm', 'cancelForm']);

    function confirm() {
        if (!socialMediaModal.value) {
            return;
        }

        socialMediaModal.value.closeOverlay();

        emit('confirmForm');
    }

    function cancel() {
        if (!socialMediaModal.value) {
            return;
        }

        socialMediaModal.value.closeOverlay();

        emit('cancelForm');
    }

    function setForm(values: Form) {
        form.value = values;
    }

    defineExpose({
        overlay: socialMediaModal,
        setForm,
        form,
    });
</script>
